import { Modal, notification, Tag } from 'antd';
import { Fragment, PureComponent, ReactNode } from 'react';
import fetchMatchingLabTests from '../../../../api/fetchMatchingLabTests';
import PadV2AutoSuggest, {
    AllOptions,
    AS_OPTIONS_SEARCH,
} from '../workspace/padV2/PadV2AutoSuggest';
import { LabVital as LabVitalIcon } from '../../../common/Icons';
import requestVitals from '../../../../api/requestVitals';
import { captureError } from '../../../../pages/captureError';
import { track } from '../../../../tracking';
import { getAclViewClassName } from '../../../../restrictions/utils';
import { SREV2 } from '../../../../api/pushGenie';

type State = {
    status?: 'success' | 'loading';
    isDialogOpen: boolean;
    vitals: { [k: string]: { id: string; name: string } };
};

type Props = {
    ptId: string;
};

class RequestVitals extends PureComponent<Props, State> {
    state: State = {
        isDialogOpen: false,
        vitals: {},
    };

    onRequest = (): void => {
        track('onRequestVitals');
        this.setState({ isDialogOpen: true });
    };

    closeDialog = (): void => {
        this.setState({ isDialogOpen: false });
    };

    getOptions = (
        setOptions: (
            options: AllOptions<{ id: string; name: string }>,
            inputValue?: string,
        ) => void,
        input?: string,
    ): void => {
        if (!input) {
            setOptions([], input);
            return;
        }

        fetchMatchingLabTests(input)
            .then(async (response) => {
                setOptions([{ label: AS_OPTIONS_SEARCH, options: response }], input);
            })
            .catch(() => {
                setOptions([], input);
            });
    };

    OptionIcon = LabVitalIcon('fill-darwin-accent-bright-yellow-800');
    OptionIconWhite = LabVitalIcon('fill-white');

    onSelect = (data: { id: string; name: string }): void => {
        const { vitals } = this.state;

        if (Object.keys(vitals).length >= 3) {
            notification.warning({
                message: 'Upto 3 vitals can be tracked at a time.',
                duration: 5,
            });
            return;
        }
        vitals[data.id] = data;
        this.setState({ vitals: { ...vitals } });
    };

    removeVital = (id: string): void => {
        const { vitals } = this.state;
        delete vitals[id];
        this.setState({ vitals: { ...vitals } });
    };

    onSave = (): void => {
        const { vitals } = this.state;
        const { ptId } = this.props;
        const vArray = Object.keys(vitals).map((id) => vitals[id]);

        track('requestVitalsSave', { requestedVitals: vArray.join(', ') });

        if (!vArray.length) {
            notification.error({
                message: 'Please select at least 1 vital to request',
                duration: 5,
            });
            return;
        }

        this.setState({
            status: 'loading',
        });
        requestVitals({ patientid: ptId, vitals: vArray })
            .then((res) => {
                if (res.success) {
                    this.setState({
                        status: 'success',
                        isDialogOpen: false,
                    });
                    notification.success({
                        message: 'Successfully requested vitals from patient',
                        duration: 5,
                    });
                } else {
                    throw new Error(res.error);
                }
            })
            .catch((e) => {
                captureError(e);
                notification.error({
                    message: (e as Error).message,
                    duration: 5,
                });
                this.setState({
                    status: undefined,
                });
            });
    };

    render(): ReactNode {
        const { status, isDialogOpen, vitals } = this.state;
        return (
            <Fragment>
                <Modal
                    title="Request Vitals"
                    visible={isDialogOpen}
                    onOk={this.onSave}
                    okButtonProps={{
                        className: 'primary-btn font-lato cursor-pointer',
                        loading: status === 'loading',
                    }}
                    onCancel={this.closeDialog}
                    bodyStyle={{ height: 400 }}
                    okText={'Save'}
                >
                    <div style={{ marginTop: -12 }}>
                        <PadV2AutoSuggest
                            onSelect={this.onSelect}
                            optionIconText={this.OptionIcon}
                            optionIconBgClass="circle-lx-reading"
                            menuListClassName="lx-reading-dropdown"
                            optionWrapperClass="lx-reading-dropdown-item"
                            getOptions={this.getOptions}
                            placeholder="Start typing a vital name..."
                            elements={[]}
                        />
                    </div>
                    <div className="mt-8 flex flex-col">
                        {Object.keys(vitals).map((id) => (
                            <div id={id} key={id}>
                                <div>
                                    <div className="flex items-center mt-8 mr-8">
                                        <span className="sy-chip flex">
                                            {vitals[id].name}

                                            <div
                                                className="flex justify-end items-center w-12 h-20 w-20 cursor-pointer"
                                                onClick={() => this.removeVital(id)}
                                            >
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    width="9"
                                                    height="11"
                                                    viewBox="0 0 9 11"
                                                    fill="none"
                                                    className="ml-8"
                                                >
                                                    <path
                                                        d="M1.03516 9.37109C1.05469 9.85938 1.48438 10.25 1.97266 10.25H6.75781C7.24609 10.25 7.67578 9.85938 7.69531 9.37109L8.125 2.75H0.625L1.03516 9.37109ZM8.4375 0.875H6.25L6.01562 0.425781C5.95703 0.328125 5.85938 0.25 5.74219 0.25H2.98828C2.87109 0.25 2.77344 0.328125 2.71484 0.425781L2.5 0.875H0.3125C0.136719 0.875 0 1.03125 0 1.1875V1.8125C0 1.98828 0.136719 2.125 0.3125 2.125H8.4375C8.59375 2.125 8.75 1.98828 8.75 1.8125V1.1875C8.75 1.03125 8.59375 0.875 8.4375 0.875Z"
                                                        fill="#B04E43"
                                                    />
                                                </svg>
                                            </div>
                                        </span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </Modal>

                <span
                    onClick={this.onRequest}
                    role="button"
                    className={`font-600 rounded-24 inline-flex items-center justify-center cursor-pointer bg-actionPrimary text-white hover:bg-actionPrimary-hover active:bg-actionPrimary-active disabled:bg-actionPrimary-disabled px-12 py-4 text-14 chip-show mt-16 mr-16 space-x-8 ${getAclViewClassName(
                        SREV2.REQUEST_VITALS,
                    )}`}
                >
                    <span>
                        {status === 'success' ? (
                            '✓'
                        ) : status === 'loading' ? (
                            <div className="w-16 h-16 flex items-center justify-center">
                                <svg
                                    viewBox="0 0 512 512"
                                    className="fill-white animate-spin"
                                    width="20"
                                    height="20"
                                >
                                    <path d="M456.433 371.72l-27.79-16.045c-7.192-4.152-10.052-13.136-6.487-20.636 25.82-54.328 23.566-118.602-6.768-171.03-30.265-52.529-84.802-86.621-144.76-91.424C262.35 71.922 256 64.953 256 56.649V24.56c0-9.31 7.916-16.609 17.204-15.96 81.795 5.717 156.412 51.902 197.611 123.408 41.301 71.385 43.99 159.096 8.042 232.792-4.082 8.369-14.361 11.575-22.424 6.92z" />
                                </svg>
                            </div>
                        ) : (
                            <div className="w-16 h-16 flex items-center justify-center">
                                {this.OptionIconWhite}
                            </div>
                        )}{' '}
                    </span>
                    <p>{status === 'success' ? 'Vitals requested' : 'Request Vitals'}</p>
                </span>
            </Fragment>
        );
    }
}

export default RequestVitals;
